import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createUserWithEmailAndPassword, onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { auth, db, functions, httpsCallable } from "/Users/michaeltucker/Documents/coding-projects/client/src/firebase.js";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { createShareCode } from "RandomId";
import {
  buildDefaultAnnouncements,
  buildDefaultClasses,
  buildDefaultSettings,
  convertEmailToLowercase,
  formatDateYYYYMMDD,
  getDate30DaysInFuture
} from "utils/signupDefaults";
import { createDefaultProductTourState } from "utils/productTour";
import { createNotApplicableLegacyScheduleJuly2026ConversionState } from "utils/legacyScheduleJuly2026Conversion";
import { initializeNewUserSchedulingData } from "utils/scheduleCalendarFirestore";
import GoogleSignInButton from "components/GoogleSignInButton";
import {
  AUTH_PROVIDER_IDS,
  getGoogleAuthErrorMessage,
  getGoogleRedirectResult,
  signInWithGooglePopupOrRedirect
} from "utils/authProviderMethods";
import { trackAuthAnalyticsEvent } from "utils/authTelemetry";
import { captureSignupCountryAfterRegistration } from "utils/captureSignupCountry";
import {
  bootstrapUserShellAfterSignup,
  ensureUserDocumentForAuthUser
} from "utils/userProvisioning";
import { getSignupMobileLeadValue, getSignupRuntimeDeviceLeadFields } from "utils/signupDevice";
import {
  buildAttributionSummary,
  captureAdAttribution,
  getSignupAttribution,
  META_REGISTRATION_TRACKING_STATUS_ELIGIBLE,
  trackRegistrationSuccess
} from "utils/metaPixel";
import { buildSignupCountrySignals } from "utils/countryAttribution";
import ReferralSignupBanner from "components/ReferralSignupBanner";
import { captureReferralFromSearch } from "utils/referralStorage";
import { attachStoredReferralAfterSignup } from "utils/referralSignup";
import { getMobileSignupRedirectPath } from "utils/mobileSignupHandoff";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Spinner
} from "reactstrap";

const SCHOOL_ID_REGEX = /^[a-zA-Z0-9]{7}$/;

function isEmailValid(email) {
  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailPattern.test(email);
}

function getOrdinalSuffix(dayNumber) {
  if (dayNumber >= 11 && dayNumber <= 13) return "th";
  const remainder = dayNumber % 10;
  if (remainder === 1) return "st";
  if (remainder === 2) return "nd";
  if (remainder === 3) return "rd";
  return "th";
}

function formatDateMonthDayOrdinal(value) {
  let date = null;
  if (value?.toDate) {
    date = value.toDate();
  } else if (value instanceof Date) {
    date = value;
  }

  if (!date || Number.isNaN(date.getTime())) return "a date in the future";

  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  return `${month} ${day}${getOrdinalSuffix(day)}`;
}

function toDateFromFirestoreValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  return null;
}

function buildSchoolTags({ isSiteLicense, endDate, existingTags = [] }) {
  const schoolTagKeys = new Set([
    "School Pilot",
    "School Site License",
    "School Multi-Seat License",
    "School Pilot Active",
    "School Pilot Expired",
    "School Site Active",
    "School Site Expired",
    "School Multi-Seat Active",
    "School Multi-Seat Expired"
  ]);

  const normalizedLicenseType = ["site", "multi-seat"].includes(isSiteLicense) ? isSiteLicense : "pilot";
  const licenseTag = normalizedLicenseType === "site"
    ? "School Site License"
    : (normalizedLicenseType === "multi-seat" ? "School Multi-Seat License" : "School Pilot");
  const activeTag = normalizedLicenseType === "site"
    ? "School Site Active"
    : (normalizedLicenseType === "multi-seat" ? "School Multi-Seat Active" : "School Pilot Active");
  const expiredTag = normalizedLicenseType === "site"
    ? "School Site Expired"
    : (normalizedLicenseType === "multi-seat" ? "School Multi-Seat Expired" : "School Pilot Expired");
  const cleanedTags = (Array.isArray(existingTags) ? existingTags : []).filter((tag) => !schoolTagKeys.has(tag));

  const result = [...cleanedTags, licenseTag];
  const endAsDate = toDateFromFirestoreValue(endDate);
  if (endAsDate && endAsDate.getTime() < Date.now()) {
    result.push(expiredTag);
  } else {
    result.push(activeTag);
  }
  return [...new Set(result)];
}

function SchoolRegister() {
  const { schoolId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const enrollmentSecret = useMemo(
    () => new URLSearchParams(location.search).get("key")?.trim() || "",
    [location.search]
  );

  const [school, setSchool] = useState(null);
  const [schoolLoading, setSchoolLoading] = useState(true);
  const [schoolError, setSchoolError] = useState("");
  const [signupError, setSignupError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [leadSource, setLeadSource] = useState("School Signup");
  const [showValidation, setShowValidation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSignInMode, setIsSignInMode] = useState(false);
  const [checkingExistingMembership, setCheckingExistingMembership] = useState(true);
  const [hasJoinedCurrentSchool, setHasJoinedCurrentSchool] = useState(false);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  const [currentAuthUser, setCurrentAuthUser] = useState(null);
  const [checkedGoogleRedirect, setCheckedGoogleRedirect] = useState(false);

  const normalizedSchoolId = useMemo(
    () => (schoolId || "").trim().toLowerCase(),
    [schoolId]
  );

  const normalizedSchoolLicenseType = (school?.licenseType || "pilot").toLowerCase();
  const isPaidSchoolLicense = ["site", "multi-seat"].includes(normalizedSchoolLicenseType);

  useEffect(() => {
    captureAdAttribution(window.location.search);
    captureReferralFromSearch(window.location.search);
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchSchool() {
      setSchoolLoading(true);
      setSchoolError("");
      setSchool(null);

      if (!SCHOOL_ID_REGEX.test((schoolId || "").trim())) {
        if (isMounted) {
          setSchoolError("This school link is not valid.");
          setSchoolLoading(false);
        }
        return;
      }

      try {
        const directRef = doc(db, "schools", (schoolId || "").trim());
        const directSnap = await getDoc(directRef);

        if (directSnap.exists()) {
          if (isMounted) {
            setSchool({ id: directSnap.id, ...directSnap.data() });
            setSchoolLoading(false);
          }
          return;
        }

        const normalizedRef = doc(db, "schools", normalizedSchoolId);
        const normalizedSnap = await getDoc(normalizedRef);

        if (normalizedSnap.exists()) {
          if (isMounted) {
            setSchool({ id: normalizedSnap.id, ...normalizedSnap.data() });
            setSchoolLoading(false);
          }
          return;
        }

        if (isMounted) {
          setSchoolError("This school link is not valid.");
          setSchoolLoading(false);
        }
      } catch (error) {
        console.error("Failed to fetch pilot school:", error);
        if (isMounted) {
          setSchoolError("We could not load this school link right now. Please try again.");
          setSchoolLoading(false);
        }
      }
    }

    fetchSchool();

    return () => {
      isMounted = false;
    };
  }, [normalizedSchoolId, schoolId]);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!isMounted) return;
      setCurrentAuthUser(authUser || null);
      if (isAuthTransitioning) return;
      if (!authUser) {
        setHasJoinedCurrentSchool(false);
        setCheckingExistingMembership(false);
        return;
      }

      const resolvedSchoolId = (school?.id || normalizedSchoolId || "").trim().toLowerCase();
      if (!resolvedSchoolId) {
        setHasJoinedCurrentSchool(false);
        setCheckingExistingMembership(false);
        return;
      }

      setCheckingExistingMembership(true);
      try {
        const userSnap = await getDoc(doc(db, "usersNew", authUser.uid));
        const userSchoolId = (userSnap.data()?.schoolId || "").trim().toLowerCase();
        setHasJoinedCurrentSchool(userSchoolId === resolvedSchoolId);
      } catch (error) {
        console.error("Failed to check existing school membership:", error);
        setHasJoinedCurrentSchool(false);
      } finally {
        if (isMounted) {
          setCheckingExistingMembership(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [school?.id, normalizedSchoolId, isAuthTransitioning]);

  useEffect(() => {
    let isMounted = true;

    async function finishRedirectEnrollment() {
      if (checkedGoogleRedirect || schoolLoading || schoolError || !school) {
        return;
      }

      setCheckedGoogleRedirect(true);
      try {
        const { result, intent } = await getGoogleRedirectResult();
        if (!isMounted || !result || intent !== "school_register") {
          return;
        }

        await finishGoogleSchoolEnrollment(result.user, "school_register_redirect");
      } catch (error) {
        if (!isMounted) return;
        console.error("Google school enrollment redirect failed:", error);
        setIsAuthTransitioning(false);
        setSignupError(getSchoolEnrollmentErrorMessage(error));
      }
    }

    finishRedirectEnrollment();

    return () => {
      isMounted = false;
    };
  }, [checkedGoogleRedirect, schoolLoading, schoolError, school]);

  function isValidated() {
    if (!firstName || !lastName || !email || !password) return false;
    if (!isEmailValid(email)) return false;
    if (password.length < 8) return false;
    if (firstName.length <= 0 || firstName.length > 30) return false;
    if (lastName.length <= 0 || lastName.length > 30) return false;
    return true;
  }

  async function waitForUserData(userId, maxRetries = 10, delay = 500) {
    const userRef = doc(db, "usersNew", userId);

    for (let i = 0; i < maxRetries; i += 1) {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) return;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  async function attachUserToSchoolViaCallable({
    userId,
    teacherName,
    teacherEmail
  }) {
    const resolvedSchoolId = school?.id || normalizedSchoolId;
    const attribution = getSignupAttribution();
    const countrySignals = buildSignupCountrySignals();
    const callable = httpsCallable(functions, "attachUserToSchoolLicense");
    await callable({
      uid: userId,
      schoolId: resolvedSchoolId,
      teacherName,
      teacherEmail,
      attribution: attribution || null,
      countryAttribution: {
        browserLocale: countrySignals.browserLocale || null,
        browserTimezone: countrySignals.browserTimezone || null
      },
      enrollmentSecret: enrollmentSecret || undefined,
    });
  }

  function getSchoolEnrollmentErrorMessage(error) {
    if (error?.code === "functions/permission-denied") {
      return "This school license link is missing or invalid. Please use the link from your school administrator.";
    }
    if (error?.code === "functions/not-found") {
      return "We could not find this school or account. Please contact us for help.";
    }
    if (error?.code === "functions/failed-precondition") {
      return error?.message || "This account cannot be added to the school license right now.";
    }
    if (String(error?.code || "").startsWith("auth/")) {
      return getGoogleAuthErrorMessage(error);
    }
    return error?.message || "Unable to join this school right now. Please try again.";
  }

  function buildSchoolSignupProfile(authUser) {
    const signupAttribution = getSignupAttribution();
    const countrySignals = buildSignupCountrySignals();

    return {
      email: authUser?.email,
      leadSource: isPaidSchoolLicense ? "School License" : "School Pilot",
      attribution: signupAttribution || null,
      attributionSummary: buildAttributionSummary(signupAttribution),
      countryAttribution: {
        browserLocale: countrySignals.browserLocale || null,
        browserTimezone: countrySignals.browserTimezone || null
      }
    };
  }

  async function finishGoogleSchoolEnrollment(authUser, source = "school_register_google") {
    if (!school) {
      setSignupError("This school link is not valid.");
      return;
    }

    if (!authUser?.uid) {
      setSignupError("Unable to read your Google account. Please try again.");
      return;
    }

    setSubmitting(true);
    setIsAuthTransitioning(true);
    try {
      const { created, profile } = await ensureUserDocumentForAuthUser(
        authUser,
        buildSchoolSignupProfile(authUser)
      );
      const teacherName = `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
      const teacherEmail = profile.email || authUser.email || "";

      await attachUserToSchoolViaCallable({
        userId: authUser.uid,
        teacherName,
        teacherEmail
      });

      if (created) {
        await attachStoredReferralAfterSignup();
        await captureSignupCountryAfterRegistration();
        await trackRegistrationSuccess({
          uid: authUser.uid,
          email: teacherEmail,
          firstName: profile.firstName,
          lastName: profile.lastName,
          triggerSource: "school_register_google",
          registrationMethod: "google",
        });
        await bootstrapUserShellAfterSignup(dispatch, authUser.uid);
      } else if (source !== "school_register_signed_in") {
        trackAuthAnalyticsEvent("login_google", {
          providerId: AUTH_PROVIDER_IDS.GOOGLE,
          source,
        });
      }

      history.push(created ? getMobileSignupRedirectPath() : "/");
    } catch (error) {
      console.error("Google school enrollment failed:", error);
      setIsAuthTransitioning(false);
      setSignupError(getSchoolEnrollmentErrorMessage(error));
    } finally {
      setSubmitting(false);
      setIsGoogleAuthLoading(false);
    }
  }

  async function handleGoogleSchoolEnrollment() {
    setSignupError("");
    setIsGoogleAuthLoading(true);

    try {
      const { result, redirected } = await signInWithGooglePopupOrRedirect("school_register");
      if (redirected) {
        return;
      }

      await finishGoogleSchoolEnrollment(result.user);
    } catch (error) {
      console.error("Google school enrollment failed:", error);
      setIsAuthTransitioning(false);
      setIsGoogleAuthLoading(false);
      setSignupError(getSchoolEnrollmentErrorMessage(error));
    }
  }

  async function joinSchoolAsCurrentUser() {
    setSignupError("");
    await finishGoogleSchoolEnrollment(currentAuthUser, "school_register_signed_in");
  }

  async function register(e) {
    e.preventDefault();
    setSignupError("");

    if (!school) {
      setSignupError("This school link is not valid.");
      return;
    }

    if (!isValidated()) {
      setShowValidation(true);
      return;
    }

    setSubmitting(true);
    setIsAuthTransitioning(true);
    try {
      const resolvedSignupLicenseType = ["site", "multi-seat"].includes(normalizedSchoolLicenseType)
        ? normalizedSchoolLicenseType
        : "pilot";
      const normalizedEmail = convertEmailToLowercase(email);
      const authUser = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const userId = authUser.user.uid;
      const signupAttribution = getSignupAttribution();
      const attributionSummary = buildAttributionSummary(signupAttribution);
      const countrySignals = buildSignupCountrySignals();

      const announcements = buildDefaultAnnouncements();
      const settings = buildDefaultSettings();
      const classes = buildDefaultClasses();
      const orderOfClasses = classes.map((cls) => cls.classId);

      const runtimeDeviceLeadFields = getSignupRuntimeDeviceLeadFields();

      // Persist normal signup data + pilot metadata in one user document.
      await setDoc(doc(db, "usersNew", userId), {
        firstName,
        lastName,
        email: normalizedEmail,
        accountCreated: serverTimestamp(),
        premium: false,
        trial: true,
        tags: buildSchoolTags({
          isSiteLicense: resolvedSignupLicenseType,
          endDate: school.endDate || null,
          existingTags: ["Trial"]
        }),
        shareCode: createShareCode(),
        uid: userId,
        leadData: {
          leadSource: isPaidSchoolLicense ? "School License" : "School Pilot",
          mobile: getSignupMobileLeadValue(),
          ...runtimeDeviceLeadFields,
          attributionSummary
        },
        attribution: signupAttribution || null,
        countryAttribution: {
          browserLocale: countrySignals.browserLocale || null,
          browserTimezone: countrySignals.browserTimezone || null
        },
        productTour: createDefaultProductTourState(),
        legacyScheduleJuly2026Conversion: createNotApplicableLegacyScheduleJuly2026ConversionState(),
        metaRegistrationTrackingStatus: META_REGISTRATION_TRACKING_STATUS_ELIGIBLE,
        announcements,
        orderOfClasses,
        settings,
        schoolId: school.id || normalizedSchoolId,
        schoolLicenseType: resolvedSignupLicenseType,
        // Existing app admin tooling already recognizes this as an explicit trial end date.
        newTrialEndDate: school.endDate || null,
        provisioningIncomplete: false,
        provisionedBy: "schoolRegister",
      });

      await initializeNewUserSchedulingData(userId, settings);

      for (const classData of classes) {
        const { contentBoxes, classId, ...classWithoutBoxes } = classData;
        const contentBoxesOrder = (contentBoxes || []).map((box) => box.contentBoxId);
        const classRef = doc(db, `usersNew/${userId}/classes`, classId);

        await setDoc(classRef, {
          classId,
          ...classWithoutBoxes,
          contentBoxesOrder
        });

        if (contentBoxes?.length) {
          for (const box of contentBoxes) {
            await setDoc(
              doc(db, `usersNew/${userId}/classes/${classId}/contentBoxes`, box.contentBoxId),
              box
            );
          }
        }
      }

      await attachUserToSchoolViaCallable({
        userId,
        teacherName: `${firstName} ${lastName}`.trim(),
        teacherEmail: normalizedEmail
      });

      await waitForUserData(userId);
      await attachStoredReferralAfterSignup();
      await trackRegistrationSuccess({
        uid: userId,
        email: normalizedEmail,
        firstName,
        lastName,
        triggerSource: "school_register",
        registrationMethod: "email_password",
      });
      await bootstrapUserShellAfterSignup(dispatch, userId);
      history.push(getMobileSignupRedirectPath());
    } catch (error) {
      console.error("Signup failed:", error);
      setIsAuthTransitioning(false);
      if (error?.code === "auth/email-already-in-use") {
        setSignupError("An account with this email already exists. Use the sign-in button below to join this school.");
      } else {
        setSignupError(error?.message || "Registration failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function signInAndJoinSchool() {
    setSignupError("");

    if (!school) {
      setSignupError("This school link is not valid.");
      return;
    }

    if (!email || !password || !isEmailValid(email)) {
      setShowValidation(true);
      setSignupError("Enter a valid email and password to sign in.");
      return;
    }

    setSubmitting(true);
    setIsAuthTransitioning(true);
    try {
      const normalizedEmail = convertEmailToLowercase(email);
      const authUser = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const userId = authUser.user.uid;

      await attachUserToSchoolViaCallable({
        userId,
        teacherName: `${firstName} ${lastName}`.trim(),
        teacherEmail: normalizedEmail
      });

      history.push("/");
    } catch (error) {
      console.error("Sign in and join school failed:", error);
      setIsAuthTransitioning(false);
      if (error?.code === "auth/wrong-password" || error?.code === "auth/invalid-credential") {
        setSignupError("Incorrect password for this account. Please try again.");
      } else if (error?.code === "auth/user-not-found") {
        setSignupError("No account exists for this email yet. Use Create School Account instead.");
      } else {
        setSignupError("Unable to sign in and join this school right now. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="register-page" style={{ minHeight: "100vh", background: "#ffffff" }}>
      <Container>
        <Row style={{ minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
          <Col md="9" lg="7" xl="6">
            <Card className="card-signup text-center">
              <CardHeader style={{ minHeight: "20px" }}>
                <img
                  src="/square-logo-v2.png"
                  alt="Lesson Launchpad"
                  style={{ width: "90px", height: "auto", margin: "10px auto 6px auto", display: "block" }}
                />
                <h2 style={{ fontWeight: "bold", color: "#004aad", margin: "10px 0px" }}>
                  Create Your Account
                </h2>
              </CardHeader>
              <CardBody>
                {schoolLoading || checkingExistingMembership || isAuthTransitioning ? (
                  <div style={{ padding: "30px 0" }}>
                    <Spinner color="info" style={{ width: "2.5rem", height: "2.5rem" }} />
                    <div style={{ marginTop: "12px" }}>
                      {schoolLoading
                        ? "Loading school details..."
                        : (isAuthTransitioning ? "Finalizing your account..." : "Checking account...")}
                    </div>
                  </div>
                ) : schoolError ? (
                  <Alert color="danger" style={{ marginBottom: 0 }}>
                    This school link is not valid.
                  </Alert>
                ) : hasJoinedCurrentSchool ? (
                  <>
                    <div style={{ marginBottom: "16px", lineHeight: 1.45 }}>
                      <strong>{school?.schoolName}</strong>{" "}
                      {isPaidSchoolLicense
                        ? "has a Lesson Launchpad™ school license."
                        : "is participating in a Lesson Launchpad™ pilot."}
                    </div>
                    <Alert color="success" style={{ marginBottom: "8px" }}>
                      You've already successfully joined {isPaidSchoolLicense ? "this school license" : "the pilot"}.
                    </Alert>
                    <div style={{ color: "#3a3a3a", fontSize: "0.95rem" }}>
                      Click below to return to Lesson Launchpad.
                    </div>
                  </>
                ) : currentAuthUser?.uid ? (
                  <>
                    <div style={{ marginBottom: "16px", lineHeight: 1.45 }}>
                      <strong>{school?.schoolName}</strong>{" "}
                      {isPaidSchoolLicense
                        ? "has a Lesson Launchpad™ school license."
                        : "is participating in a Lesson Launchpad™ pilot."}
                    </div>
                    <Alert color="info" style={{ marginBottom: "8px" }}>
                      You are signed in as {currentAuthUser.email || "your current account"}.
                    </Alert>
                    {signupError ? <Alert color="danger">{signupError}</Alert> : null}
                    <div style={{ color: "#3a3a3a", fontSize: "0.95rem" }}>
                      Click below to join {isPaidSchoolLicense ? "this school license" : "the pilot"} with this account.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: "16px", lineHeight: 1.45 }}>
                      <strong>{school?.schoolName}</strong>{" "}
                      {isPaidSchoolLicense
                        ? "has a Lesson Launchpad™ school license."
                        : "is participating in a Lesson Launchpad™ pilot."}
                      <br />
                      {!isPaidSchoolLicense && (
                        <>
                      Teachers are invited to use Lesson Launchpad through{" "}
                      {formatDateMonthDayOrdinal(school?.endDate)}.
                      <br />
                      </>
                      )}
                      <br />
                      Lesson Launchpad helps automate common classroom tasks like timers, reminders,
                      transitions, announcements, and agendas so teachers can focus on teaching.
                    </div>

                    <div style={{ marginBottom: "18px", color: "#3a3a3a", fontSize: "0.95rem" }}>
                      {isSignInMode
                        ? (isPaidSchoolLicense ? "Sign in below to join." : "Sign in below to join the pilot.")
                        : (isPaidSchoolLicense ? "Create your account below to join." : "Create your account below to participate in the pilot.")}
                    </div>

                    {signupError ? <Alert color="danger">{signupError}</Alert> : null}

                    {!isSignInMode ? <ReferralSignupBanner /> : null}

                    <Form>
                      {!isSignInMode ? (
                        <>
                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText style={{ borderRadius: "8px 0px 0px 8px" }}>
                                <i className="nc-icon nc-single-02" />
                              </InputGroupText>
                            </InputGroupAddon>
                            <Input
                              onChange={(evt) => setFirstName(evt.target.value)}
                              placeholder="First Name..."
                              type="text"
                              autoComplete="given-name"
                              required
                              valid={showValidation && firstName?.length > 0 && firstName?.length <= 30}
                              invalid={showValidation && (!firstName || firstName.length > 30)}
                              style={{ borderRadius: "0px 8px 8px 0px" }}
                            />
                            {showValidation && (!firstName || firstName.length > 30) ? (
                              <FormFeedback invalid>
                                {!firstName ? "Please enter a first name." : "First name must be 30 characters or fewer."}
                              </FormFeedback>
                            ) : (
                              <FormFeedback valid>Looks good!</FormFeedback>
                            )}
                          </InputGroup>

                          <InputGroup>
                            <InputGroupAddon addonType="prepend">
                              <InputGroupText style={{ borderRadius: "8px 0px 0px 8px" }}>
                                <i className="nc-icon nc-circle-10" />
                              </InputGroupText>
                            </InputGroupAddon>
                            <Input
                              onChange={(evt) => setLastName(evt.target.value)}
                              placeholder="Last Name..."
                              type="text"
                              autoComplete="family-name"
                              required
                              valid={showValidation && lastName?.length > 0 && lastName?.length <= 30}
                              invalid={showValidation && (!lastName || lastName.length > 30)}
                              style={{ borderRadius: "0px 8px 8px 0px" }}
                            />
                            {showValidation && (!lastName || lastName.length > 30) ? (
                              <FormFeedback invalid>
                                {!lastName ? "Please enter a last name." : "Last name must be 30 characters or fewer."}
                              </FormFeedback>
                            ) : (
                              <FormFeedback valid>Looks good!</FormFeedback>
                            )}
                          </InputGroup>
                        </>
                      ) : null}

                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText style={{ borderRadius: "8px 0px 0px 8px" }}>
                            <i className="nc-icon nc-email-85" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          onChange={(evt) => setEmail(evt.target.value)}
                          placeholder="Email..."
                          type="email"
                          autoComplete="email"
                          required
                          valid={showValidation && email && isEmailValid(email)}
                          invalid={showValidation && !isEmailValid(email)}
                          style={{ borderRadius: "0px 8px 8px 0px" }}
                        />
                        {showValidation && !isEmailValid(email) ? (
                          <FormFeedback invalid>Email is invalid.</FormFeedback>
                        ) : (
                          <FormFeedback valid>Looks good!</FormFeedback>
                        )}
                      </InputGroup>

                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText style={{ borderRadius: "8px 0px 0px 8px" }}>
                            <i className="nc-icon nc-key-25" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          onChange={(evt) => setPassword(evt.target.value)}
                          placeholder="Password..."
                          type={showPassword ? "text" : "password"}
                          autoComplete={isSignInMode ? "current-password" : "new-password"}
                          required
                          valid={showValidation && (isSignInMode ? password?.length > 0 : password?.length >= 8)}
                          invalid={showValidation && (isSignInMode ? !password : (!password || password.length < 8))}
                          style={{ borderRadius: "0px", borderRight: 0 }}
                        />
                        {showValidation && (isSignInMode ? !password : (!password || password.length < 8)) ? (
                          <FormFeedback invalid>
                            {isSignInMode ? "Please enter your password." : "Password must be at least 8 characters."}
                          </FormFeedback>
                        ) : (
                          <FormFeedback valid>Looks good!</FormFeedback>
                        )}
                        <InputGroupAddon addonType="append">
                          <InputGroupText
                            style={{ cursor: "pointer", borderRadius: "0px 8px 8px 0px", paddingRight: "10px" }}
                            onClick={() => setShowPassword((prev) => !prev)}
                          >
                            <i className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`} />
                          </InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Form>

                    <div style={{ margin: "18px auto 0", maxWidth: "330px" }}>
                      <div className="auth-login-divider">
                        <span>or</span>
                      </div>
                      <GoogleSignInButton
                        onClick={handleGoogleSchoolEnrollment}
                        loading={isGoogleAuthLoading}
                        disabled={submitting}
                      />
                    </div>
                  </>
                )}
              </CardBody>

              {!schoolLoading && !schoolError && !checkingExistingMembership && !isAuthTransitioning ? (
                <CardFooter style={{ marginTop: '0px', paddingTop: '0px' }}>
                  {hasJoinedCurrentSchool ? (
                    <Button
                      className="button-fixed"
                      color="info"
                      onClick={() => history.push("/")}
                      style={{
                        padding: "10px 30px",
                        borderRadius: "30px",
                        fontSize: "1.1rem",
                        fontWeight: "bold"
                      }}
                    >
                      Lesson Launchpad
                    </Button>
                  ) : currentAuthUser?.uid ? (
                    <Button
                      className="button-fixed"
                      color="info"
                      onClick={joinSchoolAsCurrentUser}
                      disabled={submitting}
                      style={{
                        padding: "10px 30px",
                        borderRadius: "30px",
                        fontSize: "1.1rem",
                        fontWeight: "bold"
                      }}
                    >
                      {submitting ? "Joining..." : `Join as ${currentAuthUser.email || "Current User"}`}
                    </Button>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                      <Button
                        className="button-fixed"
                        color="info"
                        onClick={isSignInMode ? signInAndJoinSchool : register}
                        disabled={submitting}
                        style={{
                          padding: "10px 30px",
                          borderRadius: "30px",
                          fontSize: "1.1rem",
                          fontWeight: "bold"
                        }}
                      >
                        {submitting ? (isSignInMode ? "Signing in..." : "Creating account...") : (isSignInMode ? "Sign In" : "Create Account")}
                      </Button>
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ marginBottom: "5px" }}>
                          {isSignInMode ? (
                            <>Need an account? <span style={{ color: "#004aad", cursor: "pointer", textDecoration: "underline" }} onClick={() => { setIsSignInMode(false); setSignupError(""); setShowValidation(false); }}>Create Account</span></>
                          ) : (
                            <>Already have an account? <span style={{ color: "#004aad", cursor: "pointer", textDecoration: "underline" }} onClick={() => { setIsSignInMode(true); setSignupError(""); setShowValidation(false); }}>Sign In</span></>
                          )}
                        </p>
                        <p>Having trouble? <a href="/contact">Contact us</a></p>
                      </div>
                    </div>
                  )}
                </CardFooter>
              ) : null}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default SchoolRegister;
